<script lang="ts">
	import { format } from 'svelte-i18n';
	//Types
	import type { DeviceStatusG2 } from 'src/types/DeviceStatusG2';
	import type { Device } from 'src/types/Device';
	import type { DeviceSettings } from '../Scenes/helpers/device_conditions/custom_cases/presence/presence-device-helpers';
	//Design system components
	import { ButtonV2, CircleAvatar, Text } from '@Shelly/shelly-design-system/index';
	// Helpers
	import { capFirstChar } from 'src/helpers/utilities';
	import { navigateToSetting } from 'src/stores/ui/navigate-to';
	import {
		checkNoDigitalIONotConfigured,
		checkNoTemperature,
		isPillSerialMode,
	} from 'src/helpers/thepill/thepill_data_helpers';
	import { getVirtualComponentsInterface } from 'src/helpers/virtual_helpers/virtual_components_helpers';

	export let mode: string = null,
		device: Device = null,
		status: DeviceStatusG2 = null,
		settings: DeviceSettings & { pill?: any } = null;
	/** When provided (e.g. from Sidepanel Main), used instead of navigateToSetting for Peripherals settings. Keeps drawer behaviour when omitted. */
	export let onPeripheralsSettingsClick: (() => void) | undefined;

	const temperatureModes = ['onewire', 'dht22'];

	$: isTemperatureMode = temperatureModes.includes(mode) && checkNoTemperature(status);
	$: isDigitalIONotConfigured = checkNoDigitalIONotConfigured(settings);
	$: hasVirtualComponent = getVirtualComponentsInterface(device?.id).filter(
		(i) => (i?.type as string) === 'group',
	)?.length;

	const sensorRequired = mode === 'onewire' ? 'DS18B20' : 'DHT22';
</script>

<div class="flex-full-height align-items-center justify-content-center pt-l pb-l">
	{#if isTemperatureMode}
		<CircleAvatar disabled class="mb-m" size="xxl" icon="fa fa-temperature-low" />

		<Text class="mb-s text-bold text-center">
			{$format('device_message_no_temperature_sensor', {
				default: 'Temperature sensor not connected.',
			})}
		</Text>
		<Text class="text-color-muted text-center" styleType="body-small">
			{capFirstChar(
				$format('text_peripheral_sensor_supported', {
					default: `Supported peripheral sensor: {sensor}.`,
					values: { sensor: sensorRequired },
				}),
			)}
		</Text>

		<Text class="mt-m text-center" styleType="body-small">
			{capFirstChar(
				$format('text_peripheral_temperature_sensor_instruction', {
					default: `Please connect the temperature sensor to the device or configure The Pill peripherals.`,
				}),
			)}
		</Text>
		<ButtonV2
			class="mt-m"
			variant="primary"
			on:click={() =>
				onPeripheralsSettingsClick
					? onPeripheralsSettingsClick()
					: navigateToSetting(
							device.id,
							'pill_peripheral_settings',
							'pill_peripheral_settings',
						)}
			>{capFirstChar($format('button_configure', { default: 'Configure' }))}</ButtonV2
		>
	{:else if isDigitalIONotConfigured}
		<CircleAvatar disabled class="mb-m" size="xxl" icon="fa fa-sensor-triangle-exclamation" />
		<Text class="mb-s text-bold text-center">
			{$format('device_message_no_digital_io', {
				default: 'Input/Output not configured.',
			})}
		</Text>
		<Text class="text-color-muted text-center" styleType="body-small">
			{capFirstChar(
				$format('text_peripheral_digital_io_required', {
					default: `Please configure your digital Inputs/Outputs.`,
				}),
			)}
		</Text>
		<ButtonV2
			class="mt-m"
			variant="primary"
			on:click={() =>
				onPeripheralsSettingsClick
					? onPeripheralsSettingsClick()
					: navigateToSetting(
							device.id,
							'pill_peripheral_settings',
							'pill_peripheral_settings',
						)}
			>{capFirstChar($format('button_configure', { default: 'Configure' }))}</ButtonV2
		>
	{:else if isPillSerialMode(mode) && !hasVirtualComponent}
		<CircleAvatar disabled class="mb-m" size="xxl" icon="fa fa-arrow-down-arrow-up" />
		<Text class="mb-s text-bold text-center">
			{$format('device_message_peripheral_serial_title', {
				default: 'Serial Communication',
			})}
		</Text>
		<Text class="text-color-muted text-center" styleType="body-small">
			{capFirstChar(
				mode === 'mb_client'
					? $format('text_peripheral_modbus_connect_instruction', {
							default: `Connect a Modbus RTU device via the RS485 Add-on. Use Virtual Components to display data and Scripts to process it.`,
						})
					: $format('text_peripheral_uart_connect_instruction', {
							default: `Connect an external UART device. Use Virtual Components to display data and Scripts to process it.`,
						}),
			)}
		</Text>
		<ButtonV2 class="mt-m" variant="primary" on:click={() => navigateToSetting(device.id, 'scripts')}
			>{$format('button_create_new_script', {
				default: 'Create new script',
			})}</ButtonV2
		>
		<ButtonV2
			class="mt-s"
			size="m"
			variant="secondary"
			on:click={() => navigateToSetting(device.id, 'virtual_components')}
			>{$format('virtual_components_create_component', {
				default: 'Create virtual component',
			})}</ButtonV2
		>
	{/if}
</div>
